import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  confirmInvoicePaid,
  createInvoiceShareLink,
  getJobInvoice,
  invoiceQueryKey,
  sendJobInvoice,
  updateJobInvoice,
  type InvoiceShareLinkDto,
  type UpdateInvoiceRequest,
} from '@/lib/api/invoices';
import { customersQueryKey, listCustomers } from '@/lib/api/customers';
import { listPaymentMethods, paymentMethodsQueryKey } from '@/lib/api/payment-methods';
import { paymentMethodLabel } from '@/routes/jobs/helpers';
import type { PaymentMethod } from '@/lib/api/jobs';

import { fromOptionalAmount, toOptionalAmount } from './helpers';
import { InvoiceDocument } from './invoice-document';
import { PaymentMethodsField } from './payment-methods-field';
import { ShareLinkPanel } from './share-link-panel';
import { editInvoiceSchema, type EditInvoiceInput } from './schema';

interface Props {
  businessId: string;
  jobId: string;
  invoiceId: string;
  // Owner, or an employee with `create_invoices` — may edit amounts, send, and
  // mint share links.
  canManage: boolean;
  // When true (opened right after creating this invoice), auto-mints a share
  // link on open instead of waiting for the owner to click "Share Link".
  autoShareLink?: boolean;
  onClose: () => void;
}

const DEFAULT_VALUES: EditInvoiceInput = {
  totalAmount: '',
  subtotal: '',
  tax: '',
  discount: '',
  dueDate: '',
  showTaxBreakdown: false,
  offerAllMethods: true,
  selectedMethodIds: [],
};

// Self-contained (like `quote-detail-modal.tsx`): owns its own detail query
// plus the update / send / share-link / confirm-paid mutations. Toasts +
// refetch are handled globally by the MutationCache in `lib/query-client.ts`.
export function InvoiceDetailModal({
  businessId,
  jobId,
  invoiceId,
  canManage,
  autoShareLink,
  onClose,
}: Props) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [shareLink, setShareLink] = useState<InvoiceShareLinkDto | null>(null);

  const invoiceQuery = useQuery({
    queryKey: invoiceQueryKey(businessId, jobId, invoiceId),
    queryFn: () => getJobInvoice(businessId, jobId, invoiceId),
  });

  const paymentMethodsQuery = useQuery({
    queryKey: paymentMethodsQueryKey(businessId),
    queryFn: () => listPaymentMethods(businessId),
  });

  // Only owners can list customers (endpoint is owner-gated). Used just to
  // prefill the mail/SMS recipient once a link has been minted; a missing
  // contact degrades to a blank one.
  const customersQuery = useQuery({
    queryKey: customersQueryKey(businessId, 'all'),
    queryFn: () => listCustomers(businessId),
    enabled: canManage && shareLink !== null,
  });

  const form = useForm<EditInvoiceInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(editInvoiceSchema),
  });

  useEffect(() => {
    if (!invoiceQuery.data) return;
    const inv = invoiceQuery.data;
    form.reset({
      totalAmount: String(inv.total_amount),
      subtotal: fromOptionalAmount(inv.subtotal),
      tax: fromOptionalAmount(inv.tax),
      discount: fromOptionalAmount(inv.discount),
      dueDate: inv.due_date ?? '',
      // Keep an already-itemized invoice's breakdown visible rather than
      // hiding real data; new/lump-sum invoices default to collapsed.
      showTaxBreakdown: inv.subtotal != null || inv.tax != null || inv.discount != null,
      // GET doesn't echo back the offered payment-method set, so we can't
      // prefill it — default to "offer all" and only send `payment_method_ids`
      // if the owner actually touches this section (see `handleSave`).
      offerAllMethods: true,
      selectedMethodIds: [],
    });
  }, [invoiceQuery.data, form]);

  const updateMutation = useMutation({
    mutationFn: (body: UpdateInvoiceRequest) => updateJobInvoice(businessId, jobId, invoiceId, body),
    meta: { successMessage: t.invoiceUpdated },
    onSuccess: () => setEditing(false),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendJobInvoice(businessId, jobId, invoiceId),
    meta: { successMessage: t.invoiceSentMsg },
  });

  // Minting also marks the invoice sent server-side; the global MutationCache
  // refetch picks that up, so the "Sent" phase badge updates without extra wiring.
  const shareMutation = useMutation({
    mutationFn: () => createInvoiceShareLink(businessId, jobId, invoiceId),
    meta: { successMessage: t.invoiceShareLinkReady },
    onSuccess: (data) => setShareLink(data),
  });

  // Opened right after creation: mint the share link immediately instead of
  // waiting for a separate "Share Link" click. Guarded so it fires once per
  // mount even under React's StrictMode double-invoke.
  const autoSharedRef = useRef(false);
  useEffect(() => {
    if (!autoShareLink || autoSharedRef.current) return;
    autoSharedRef.current = true;
    shareMutation.mutate();
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [autoShareLink]);

  const confirmMutation = useMutation({
    mutationFn: () => confirmInvoicePaid(businessId, jobId, invoiceId),
    meta: { successMessage: t.invoiceConfirmedMsg },
  });

  function handleSave(data: EditInvoiceInput) {
    const methodsTouched =
      form.formState.dirtyFields.offerAllMethods || form.formState.dirtyFields.selectedMethodIds;

    updateMutation.mutate({
      total_amount: Number(data.totalAmount),
      subtotal: data.showTaxBreakdown ? toOptionalAmount(data.subtotal) : undefined,
      tax: data.showTaxBreakdown ? toOptionalAmount(data.tax) : undefined,
      discount: data.showTaxBreakdown ? toOptionalAmount(data.discount) : undefined,
      due_date: data.dueDate || undefined,
      ...(methodsTouched
        ? { payment_method_ids: data.offerAllMethods ? [] : data.selectedMethodIds }
        : {}),
    });
  }

  const invoice = invoiceQuery.data;
  const isUnpaid = invoice?.status === 'unpaid';
  const isPendingConfirmation = invoice?.status === 'pending_confirmation';
  const busy =
    updateMutation.isPending ||
    sendMutation.isPending ||
    shareMutation.isPending ||
    confirmMutation.isPending;

  const showTaxBreakdown = form.watch('showTaxBreakdown');
  const offerAllMethods = form.watch('offerAllMethods');
  const selectedMethodIds = form.watch('selectedMethodIds');

  const paidViaLabel = invoice?.payment_method_id
    ? (() => {
        const method = paymentMethodsQuery.data?.find((m) => m.id === invoice.payment_method_id);
        return method ? method.label || paymentMethodLabel(method.type as PaymentMethod, t) : null;
      })()
    : null;

  const shareContact = invoice
    ? customersQuery.data?.find((c) => c.customer_user_id === invoice.customer_user_id)
    : undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">
            {invoice?.job_title ?? t.addInvoiceTitle}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {invoiceQuery.isPending ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">{t.loading}</p>
        ) : invoiceQuery.isError || !invoice ? (
          <p className="px-6 py-8 text-sm text-destructive">{t.invoiceLoadError}</p>
        ) : (
          <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-4">
            <InvoiceDocument invoice={invoice} paidViaLabel={paidViaLabel} />

            {isUnpaid && canManage && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  data-testid="invoice-edit-toggle"
                  onClick={() => setEditing((v) => !v)}
                >
                  {editing ? t.invoiceEditDone : t.invoiceEdit}
                </Button>
              </div>
            )}

            {isUnpaid && canManage && editing && (
              <form
                onSubmit={form.handleSubmit(handleSave)}
                className="flex flex-col gap-4 border-t pt-4"
              >
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showTaxBreakdown}
                    onChange={(e) =>
                      form.setValue('showTaxBreakdown', e.target.checked, { shouldDirty: true })
                    }
                    data-testid="invoice-edit-show-tax-breakdown"
                  />
                  {t.invoiceShowTaxBreakdown}
                </label>

                <div className={showTaxBreakdown ? 'grid grid-cols-2 gap-3' : ''}>
                  {showTaxBreakdown && (
                    <>
                      <Controller
                        control={form.control}
                        name="subtotal"
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="invoice-edit-subtotal">{t.invoiceSubtotal}</Label>
                            <input
                              {...field}
                              id="invoice-edit-subtotal"
                              inputMode="decimal"
                              data-testid="invoice-edit-subtotal-input"
                              className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            {fieldState.error && (
                              <p className="text-xs text-destructive">{fieldState.error.message}</p>
                            )}
                          </div>
                        )}
                      />
                      <Controller
                        control={form.control}
                        name="tax"
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="invoice-edit-tax">{t.invoiceTax}</Label>
                            <input
                              {...field}
                              id="invoice-edit-tax"
                              inputMode="decimal"
                              data-testid="invoice-edit-tax-input"
                              className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            {fieldState.error && (
                              <p className="text-xs text-destructive">{fieldState.error.message}</p>
                            )}
                          </div>
                        )}
                      />
                      <Controller
                        control={form.control}
                        name="discount"
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="invoice-edit-discount">{t.invoiceDiscount}</Label>
                            <input
                              {...field}
                              id="invoice-edit-discount"
                              inputMode="decimal"
                              data-testid="invoice-edit-discount-input"
                              className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            {fieldState.error && (
                              <p className="text-xs text-destructive">{fieldState.error.message}</p>
                            )}
                          </div>
                        )}
                      />
                    </>
                  )}
                  <Controller
                    control={form.control}
                    name="totalAmount"
                    render={({ field, fieldState }) => (
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor="invoice-edit-total">{t.invoiceTotal}</Label>
                        <input
                          {...field}
                          id="invoice-edit-total"
                          inputMode="decimal"
                          data-testid="invoice-edit-total-input"
                          className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                        {fieldState.error && (
                          <p className="text-xs text-destructive">{fieldState.error.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <Controller
                  control={form.control}
                  name="dueDate"
                  render={({ field }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="invoice-edit-due-date">{t.invoiceDueDate}</Label>
                      <input
                        {...field}
                        type="date"
                        id="invoice-edit-due-date"
                        data-testid="invoice-edit-due-date-input"
                        className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  )}
                />

                <PaymentMethodsField
                  businessId={businessId}
                  offerAll={offerAllMethods}
                  selectedIds={selectedMethodIds}
                  onToggleOfferAll={(v) =>
                    form.setValue('offerAllMethods', v, { shouldDirty: true })
                  }
                  onToggleMethod={(id) =>
                    form.setValue(
                      'selectedMethodIds',
                      selectedMethodIds.includes(id)
                        ? selectedMethodIds.filter((existing) => existing !== id)
                        : [...selectedMethodIds, id],
                      { shouldDirty: true },
                    )
                  }
                  error={form.formState.errors.selectedMethodIds?.message}
                />

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={busy}
                    data-testid="invoice-save"
                  >
                    {t.saveInvoice}
                  </Button>
                </div>
              </form>
            )}

            {/* Actions */}
            {(isUnpaid || isPendingConfirmation) && (
              <div className="flex flex-wrap gap-2 border-t pt-4">
                {isUnpaid && canManage && !invoice.sent_at && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => sendMutation.mutate()}
                    data-testid="invoice-send"
                  >
                    {t.invoiceSend}
                  </Button>
                )}
                {isUnpaid && canManage && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => shareMutation.mutate()}
                    data-testid="invoice-share-link"
                  >
                    {t.invoiceShareLink}
                  </Button>
                )}
                {isPendingConfirmation && canManage && (
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => confirmMutation.mutate()}
                    data-testid="invoice-confirm-payment"
                  >
                    {t.invoiceConfirmPayment}
                  </Button>
                )}
              </div>
            )}

            {shareLink && (
              <ShareLinkPanel
                link={shareLink}
                recipientEmail={shareContact?.email}
                recipientPhone={shareContact?.phone}
                onClose={() => setShareLink(null)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
